/**
 * AdminHsnTsnPage — Zentrale Fahrzeug-Wissensdatenbank (Jul-2026, User-Wunsch).
 * Tab 1: FIN-Präfixe (10 Zeichen) — gespeist aus Aufschlagstabelle,
 *        Leasingrückläufern und importierten Bestandsfahrzeugen; per KI anreicherbar.
 * Tab 2: HSN/TSN — per KI gelernte KBA-Schlüssel-Zuordnungen.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Database, Search, Save, Trash2, CheckCircle2, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MaintenancePlansTab from "../components/admin/MaintenancePlansTab";
import KnowledgeMailCard from "../components/admin/KnowledgeMailCard";

/* ------------------------------- Tab 1: FIN-Präfixe ---------------------- */
// Jul-2026 (User-Wunsch) — Spalten per Klick sortierbar, Standard: FIN-Präfix.
const VK_NUM_COLS = new Set(["model_year", "displacement", "vehicle_count"]);

function VinPrefixTab() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [verifiedCount, setVerifiedCount] = useState(0);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState("");
    const [sortKey, setSortKey] = useState("vin_prefix");
    const [sortDir, setSortDir] = useState(1);

    const toggleSort = (key) => {
        if (sortKey === key) setSortDir((d) => -d);
        else { setSortKey(key); setSortDir(1); }
    };

    const sorted = useMemo(() => {
        const list = [...items];
        list.sort((a, b) => {
            let va = a[sortKey]; let vb = b[sortKey];
            if (sortKey === "status") { va = a.verified ? 2 : a.ki_data ? 1 : 0; vb = b.verified ? 2 : b.ki_data ? 1 : 0; }
            if (sortKey === "sources") { va = (a.sources || []).join(", "); vb = (b.sources || []).join(", "); }
            const aEmpty = va === null || va === undefined || va === "";
            const bEmpty = vb === null || vb === undefined || vb === "";
            if (aEmpty && bEmpty) return 0;
            if (aEmpty) return 1;
            if (bEmpty) return -1;
            if (VK_NUM_COLS.has(sortKey) || sortKey === "status") return (Number(va) - Number(vb)) * sortDir;
            return String(va).localeCompare(String(vb), "de", { numeric: true, sensitivity: "base" }) * sortDir;
        });
        return list;
    }, [items, sortKey, sortDir]);

    const SortTh = ({ col, label, right }) => (
        <th
            className={`${right ? "text-right" : "text-left"} px-3 py-2 cursor-pointer select-none hover:text-gray-800 transition-colors whitespace-nowrap`}
            onClick={() => toggleSort(col)}
            title="Klicken zum Sortieren"
            data-testid={`vk-sort-${col}`}
        >
            {label}
            <span className={`ml-1 ${sortKey === col ? "text-[#111827]" : "text-gray-300"}`}>
                {sortKey === col ? (sortDir === 1 ? "▲" : "▼") : "↕"}
            </span>
        </th>
    );

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/vehicle-knowledge", { params: { q, limit: 300 } });
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
            setVerifiedCount(r.data.verified || 0);
        } catch { toast.error("Laden fehlgeschlagen"); }
        finally { setLoading(false); }
    }, [q]);
    useEffect(() => { load(); }, [load]);

    const rebuild = async () => {
        setBusy("rebuild");
        try {
            const r = await api.post("/admin/vehicle-knowledge/rebuild");
            toast.success(`Neu aufgebaut: ${r.data.created} neu, ${r.data.updated} aktualisiert (${r.data.total} gesamt)`);
            load();
        } catch { toast.error("Neuaufbau fehlgeschlagen"); }
        finally { setBusy(""); }
    };

    const enrich = async (it) => {
        setBusy(`enrich-${it.vin_prefix}`);
        try {
            const r = await api.post(`/admin/vehicle-knowledge/${it.vin_prefix}/enrich`);
            const conf = Math.round((r.data.confidence || 0) * 100);
            toast.success(conf >= 50 ? `KI-Anreicherung übernommen (${conf} % Konfidenz)` : `KI unsicher (${conf} %) — keine Felder überschrieben`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "KI-Anreicherung fehlgeschlagen"); }
        finally { setBusy(""); }
    };

    const save = async () => {
        setBusy("save");
        try {
            await api.patch(`/admin/vehicle-knowledge/${edit.vin_prefix}`, {
                manufacturer: edit.manufacturer, model: edit.model, model_code: edit.model_code,
                fuel: edit.fuel, displacement: edit.displacement, model_year: edit.model_year,
            });
            toast.success("Eintrag korrigiert & verifiziert");
            setEdit(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setBusy(""); }
    };

    const markVerified = async (it) => {
        try {
            await api.patch(`/admin/vehicle-knowledge/${it.vin_prefix}`, {});
            toast.success(`${it.vin_prefix} als geprüft bestätigt`);
            load();
        } catch { toast.error("Bestätigen fehlgeschlagen"); }
    };

    const remove = async (it) => {
        if (!window.confirm(`FIN-Präfix ${it.vin_prefix} aus der Wissensdatenbank löschen?`)) return;
        try {
            await api.delete(`/admin/vehicle-knowledge/${it.vin_prefix}`);
            toast.success("Eintrag gelöscht");
            load();
        } catch { toast.error("Löschen fehlgeschlagen"); }
    };

    return (
        <div data-testid="vin-prefix-tab">
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche: Präfix, Hersteller, Modell, Modell-Nr.…" className="pl-8 rounded-sm" data-testid="vk-search" />
                </div>
                <span className="text-xs text-gray-500" data-testid="vk-counts">{total} Präfixe · {verifiedCount} verifiziert</span>
                <Button variant="outline" onClick={rebuild} disabled={busy === "rebuild"} className="rounded-sm text-xs h-9 ml-auto" data-testid="vk-rebuild-btn">
                    {busy === "rebuild" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                    Aus System-Daten neu aufbauen
                </Button>
                <Button
                    variant="outline"
                    onClick={async () => {
                        setBusy("sync");
                        try {
                            const r = await api.post("/admin/vehicle-knowledge/sync-surcharge");
                            toast.success(`Aufschlagstabelle synchronisiert: ${r.data.rules_updated} Regel(n) aus ${r.data.verified_entries} verifizierten Einträgen aktualisiert`);
                        } catch { toast.error("Sync fehlgeschlagen"); }
                        finally { setBusy(""); }
                    }}
                    disabled={busy === "sync"}
                    className="rounded-sm text-xs h-9"
                    title="Verifizierte Modelle in die Leasing-Aufschlagstabelle übernehmen"
                    data-testid="vk-sync-surcharge-btn"
                >
                    {busy === "sync" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                    → Aufschlagstabelle
                </Button>
            </div>
            {/* Jul-2026: Hinweis zur Nacht-Anreicherung (User-Wunsch) */}
            <div className="mb-4 text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2" data-testid="vk-ki-disclaimer">
                Unvollständige Präfixe werden jede Nacht automatisch per KI angereichert (inkl. Ausstattungen).
                KI-angereicherte Daten können fehlerhaft sein — bitte prüfen und mit „✓ Geprüft" bestätigen.
            </div>
            {loading ? (
                <div className="text-center py-12 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-gray-500" data-testid="vk-empty">
                    Noch keine Einträge — klicke „Aus System-Daten neu aufbauen", um FIN-Präfixe aus Aufschlagstabelle, Leasingrückläufern und Bestand zu sammeln.
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm" data-testid="vk-table">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500">
                                <SortTh col="vin_prefix" label="FIN-Präfix (10)" />
                                <SortTh col="model_year" label="Baujahr" />
                                <SortTh col="manufacturer" label="Hersteller" />
                                <SortTh col="model" label="Modell" />
                                <SortTh col="model_code" label="Modell-Nr." />
                                <SortTh col="fuel" label="Kraftstoff" />
                                <SortTh col="displacement" label="ccm" right />
                                <SortTh col="vehicle_count" label="Fzg." right />
                                <SortTh col="sources" label="Quellen" />
                                <SortTh col="status" label="Status" />
                                <th className="text-right px-3 py-2">Aktion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((it) => {
                                const isEdit = edit && edit.vin_prefix === it.vin_prefix;
                                return (
                                    <tr key={it.vin_prefix} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`vk-row-${it.vin_prefix}`}>
                                        <td className="px-3 py-2 font-mono-num font-bold whitespace-nowrap">{it.vin_prefix}</td>
                                        {isEdit ? (
                                            <>
                                                <td className="px-3 py-1"><Input value={edit.model_year ?? ""} onChange={(e) => setEdit({ ...edit, model_year: e.target.value })} className="h-8 rounded-sm text-xs w-16" /></td>
                                                <td className="px-3 py-1"><Input value={edit.manufacturer || ""} onChange={(e) => setEdit({ ...edit, manufacturer: e.target.value })} className="h-8 rounded-sm text-xs" data-testid="vk-edit-manufacturer" /></td>
                                                <td className="px-3 py-1"><Input value={edit.model || ""} onChange={(e) => setEdit({ ...edit, model: e.target.value })} className="h-8 rounded-sm text-xs" data-testid="vk-edit-model" /></td>
                                                <td className="px-3 py-1"><Input value={edit.model_code || ""} onChange={(e) => setEdit({ ...edit, model_code: e.target.value })} className="h-8 rounded-sm text-xs w-24" /></td>
                                                <td className="px-3 py-1"><Input value={edit.fuel || ""} onChange={(e) => setEdit({ ...edit, fuel: e.target.value })} className="h-8 rounded-sm text-xs w-20" /></td>
                                                <td className="px-3 py-1"><Input value={edit.displacement ?? ""} onChange={(e) => setEdit({ ...edit, displacement: e.target.value })} className="h-8 rounded-sm text-xs w-16 text-right" /></td>
                                                <td className="px-3 py-1" colSpan={3} />
                                                <td className="px-3 py-1 text-right whitespace-nowrap">
                                                    <Button size="sm" onClick={save} disabled={busy === "save"} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-8 mr-1" data-testid="vk-save-btn">
                                                        <Save className="w-3.5 h-3.5 mr-1" /> Speichern
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => setEdit(null)} className="rounded-sm h-8">Abbrechen</Button>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="px-3 py-2 font-mono-num">{it.model_year || "—"}</td>
                                                <td className="px-3 py-2">{it.manufacturer || "—"}</td>
                                                <td className="px-3 py-2">{it.model || "—"}</td>
                                                <td className="px-3 py-2 font-mono-num">{it.model_code || "—"}</td>
                                                <td className="px-3 py-2">{it.fuel || "—"}</td>
                                                <td className="px-3 py-2 text-right font-mono-num">{it.displacement ?? "—"}</td>
                                                <td className="px-3 py-2 text-right font-mono-num font-bold">{it.vehicle_count || 0}</td>
                                                <td className="px-3 py-2 text-[10px] text-gray-500">{(it.sources || []).join(", ")}</td>
                                                <td className="px-3 py-2">
                                                    {it.verified ? (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> verifiziert</span>
                                                    ) : it.ki_data ? (
                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600" title={`KI-Daten können fehlerhaft sein — bitte prüfen.${(it.ki_data?.common_equipment || []).length ? ` Ausstattung: ${it.ki_data.common_equipment.join(", ")}` : ""}`}>KI-angereichert</span>
                                                    ) : (
                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600">gesammelt</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                                    {!it.verified && (
                                                        <Button size="sm" variant="outline" onClick={() => markVerified(it)} className="rounded-sm h-8 mr-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Daten sind korrekt — als geprüft bestätigen" data-testid={`vk-verify-btn-${it.vin_prefix}`}>
                                                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Geprüft
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant="outline" onClick={() => enrich(it)} disabled={busy === `enrich-${it.vin_prefix}`} className="rounded-sm h-8 mr-1 text-purple-700 border-purple-300 hover:bg-purple-50" title="Per KI aus dem Netz anreichern" data-testid={`vk-enrich-btn-${it.vin_prefix}`}>
                                                        {busy === `enrich-${it.vin_prefix}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => setEdit({ vin_prefix: it.vin_prefix, manufacturer: it.manufacturer || "", model: it.model || "", model_code: it.model_code || "", fuel: it.fuel || "", displacement: it.displacement ?? "", model_year: it.model_year ?? "" })} className="rounded-sm h-8 mr-1" data-testid={`vk-edit-btn-${it.vin_prefix}`}>
                                                        Prüfen
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => remove(it)} className="rounded-sm h-8 text-red-600 border-red-300 hover:bg-red-50" data-testid={`vk-delete-btn-${it.vin_prefix}`}>
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ------------------------------- Tab 2: HSN/TSN -------------------------- */
function KbaUploadPanel() {
    const [file, setFile] = useState(null);
    const [report, setReport] = useState(null);
    const [busy, setBusy] = useState(false);
    const run = async (apply) => {
        if (!file) { toast.error("Bitte zuerst eine KBA-Datei wählen"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/admin/hsn-tsn/kba-upload?apply=${apply}`, fd,
                { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
            setReport(r.data);
            toast.success(apply
                ? `Übernommen: ${r.data.neu} neu, ${r.data.geaendert} aktualisiert`
                : `Vorschau: ${r.data.neu} neu, ${r.data.geaendert} geändert, ${r.data.unveraendert} unverändert`);
        } catch (e) { toast.error(e.response?.data?.detail || "KBA-Abgleich fehlgeschlagen"); }
        finally { setBusy(false); }
    };
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4 mb-4" data-testid="kba-upload-panel">
            <h3 className="font-bold text-sm flex items-center gap-2"><Database className="w-4 h-4" /> KBA-Abgleich — aktuelle Typdaten-Datei einspielen</h3>
            <p className="text-xs text-gray-500 mt-1">CSV, Excel (XLSX/XLS) oder PDF mit Spalten HSN &amp; TSN (weitere Spalten wie Hersteller, Bezeichnung, ccm, kW, Kraftstoff werden automatisch erkannt; bei PDF wird die Tabelle automatisch ausgelesen). Erst Diff-Vorschau, dann Übernahme.</p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
                <input type="file" accept=".csv,.txt,.xlsx,.xlsm,.xls,.pdf" onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); }}
                       className="text-xs" data-testid="kba-file-input" />
                <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={busy || !file}
                        onClick={() => run(false)} data-testid="kba-preview-btn">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Search className="w-3.5 h-3.5 mr-1" />} Diff-Vorschau
                </Button>
                {report && !report.applied && (report.neu > 0 || report.geaendert > 0) && (
                    <Button size="sm" className="rounded-sm h-8 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy}
                            onClick={() => run(true)} data-testid="kba-apply-btn">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {report.neu + report.geaendert} Änderungen übernehmen
                    </Button>
                )}
            </div>
            {report && (
                <div className="mt-3 text-xs bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="kba-report">
                    <p className="font-bold">{report.applied ? "✅ Übernommen" : "Vorschau"} — {report.datei}: {report.zeilen_in_datei} Zeilen · Bestand {report.bestand}</p>
                    <p className="mt-1"><span className="text-emerald-700 font-bold">{report.neu} neu</span> · <span className="text-amber-700 font-bold">{report.geaendert} geändert</span> · {report.unveraendert} unverändert</p>
                    {(report.beispiele_geaendert || []).slice(0, 3).map((b) => (
                        <p key={`${b.hsn}-${b.tsn}`} className="mt-1 text-gray-600">{b.hsn}/{b.tsn}: {Object.entries(b._diff || {}).map(([k, v]) => `${k}: ${v.alt ?? "—"} → ${v.neu}`).join(", ")}</p>
                    ))}
                </div>
            )}
        </div>
    );
}

function HsnTsnTab() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/hsn-tsn/learned", { params: { q, limit: 200 } });
            setItems(r.data.items || []);
        } catch { toast.error("Laden fehlgeschlagen"); }
        finally { setLoading(false); }
    }, [q]);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setBusy("save");
        try {
            await api.patch(`/admin/hsn-tsn/learned/${edit.hsn}/${edit.tsn}`, {
                brand: edit.brand, model_variant: edit.model_variant,
                power_kw: edit.power_kw, displacement: edit.displacement, fuel: edit.fuel,
            });
            toast.success("Eintrag korrigiert & verifiziert");
            setEdit(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setBusy(""); }
    };

    const remove = async (it) => {
        if (!window.confirm(`KI-Daten für ${it.hsn}/${it.tsn} verwerfen?`)) return;
        try {
            await api.delete(`/admin/hsn-tsn/learned/${it.hsn}/${it.tsn}`);
            toast.success("KI-Daten verworfen");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
    };

    return (
        <div data-testid="hsn-tsn-tab">
            {/* Jul-2026 (User-Wunsch) — KBA-Abgleich: Datei hochladen → Diff-Vorschau → Übernehmen */}
            <KbaUploadPanel />
            <div className="relative mb-4 max-w-md">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche: HSN, TSN, Marke, Modell…" className="pl-8 rounded-sm" data-testid="hsn-tsn-search" />
            </div>
            {loading ? (
                <div className="text-center py-12 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-gray-500" data-testid="hsn-tsn-empty">
                    Noch keine gelernten Einträge — sie entstehen automatisch, wenn der KBA-Lookup in der Fahrzeug-Erfassung genutzt wird.
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm" data-testid="hsn-tsn-table">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500">
                                <th className="text-left px-3 py-2">HSN</th>
                                <th className="text-left px-3 py-2">TSN</th>
                                <th className="text-left px-3 py-2">Marke</th>
                                <th className="text-left px-3 py-2">Modell / Variante</th>
                                <th className="text-right px-3 py-2">kW</th>
                                <th className="text-right px-3 py-2">ccm</th>
                                <th className="text-left px-3 py-2">Kraftstoff</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-right px-3 py-2">Aktion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => {
                                const k = it.ki_data || {};
                                const isEdit = edit && edit.hsn === it.hsn && edit.tsn === it.tsn;
                                return (
                                    <tr key={`${it.hsn}-${it.tsn}`} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`hsn-tsn-row-${it.hsn}-${it.tsn}`}>
                                        <td className="px-3 py-2 font-mono-num font-bold">{it.hsn}</td>
                                        <td className="px-3 py-2 font-mono-num font-bold">{it.tsn}</td>
                                        {isEdit ? (
                                            <>
                                                <td className="px-3 py-1"><Input value={edit.brand || ""} onChange={(e) => setEdit({ ...edit, brand: e.target.value })} className="h-8 rounded-sm text-xs" data-testid="hsn-tsn-edit-brand" /></td>
                                                <td className="px-3 py-1"><Input value={edit.model_variant || ""} onChange={(e) => setEdit({ ...edit, model_variant: e.target.value })} className="h-8 rounded-sm text-xs" data-testid="hsn-tsn-edit-model" /></td>
                                                <td className="px-3 py-1"><Input value={edit.power_kw ?? ""} onChange={(e) => setEdit({ ...edit, power_kw: e.target.value })} className="h-8 rounded-sm text-xs w-16 text-right" data-testid="hsn-tsn-edit-kw" /></td>
                                                <td className="px-3 py-1"><Input value={edit.displacement ?? ""} onChange={(e) => setEdit({ ...edit, displacement: e.target.value })} className="h-8 rounded-sm text-xs w-16 text-right" data-testid="hsn-tsn-edit-ccm" /></td>
                                                <td className="px-3 py-1"><Input value={edit.fuel || ""} onChange={(e) => setEdit({ ...edit, fuel: e.target.value })} className="h-8 rounded-sm text-xs w-24" data-testid="hsn-tsn-edit-fuel" /></td>
                                                <td className="px-3 py-1" />
                                                <td className="px-3 py-1 text-right whitespace-nowrap">
                                                    <Button size="sm" onClick={save} disabled={busy === "save"} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-8 mr-1" data-testid="hsn-tsn-save-btn">
                                                        <Save className="w-3.5 h-3.5 mr-1" /> Speichern
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => setEdit(null)} className="rounded-sm h-8" data-testid="hsn-tsn-cancel-btn">Abbrechen</Button>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="px-3 py-2">{k.brand || "—"}</td>
                                                <td className="px-3 py-2">{k.model_variant || "—"}</td>
                                                <td className="px-3 py-2 text-right font-mono-num">{k.power_kw ?? "—"}</td>
                                                <td className="px-3 py-2 text-right font-mono-num">{k.displacement ?? "—"}</td>
                                                <td className="px-3 py-2">{k.fuel || "—"}</td>
                                                <td className="px-3 py-2">
                                                    {it.verified ? (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> verifiziert</span>
                                                    ) : (
                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600">KI-gelernt</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                                    <Button size="sm" variant="outline" onClick={() => setEdit({ hsn: it.hsn, tsn: it.tsn, brand: k.brand || "", model_variant: k.model_variant || "", power_kw: k.power_kw ?? "", displacement: k.displacement ?? "", fuel: k.fuel || "" })} className="rounded-sm h-8 mr-1" data-testid={`hsn-tsn-edit-btn-${it.hsn}-${it.tsn}`}>
                                                        Prüfen / Korrigieren
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => remove(it)} className="rounded-sm h-8 text-red-600 border-red-300 hover:bg-red-50" data-testid={`hsn-tsn-delete-btn-${it.hsn}-${it.tsn}`}>
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ------------------------------- Tab 3: Statistik ------------------------ */
function StatsTab() {
    const [data, setData] = useState(null);
    const [digest, setDigest] = useState(null);
    useEffect(() => {
        api.get("/admin/vehicle-knowledge/stats").then((r) => setData(r.data)).catch(() => toast.error("Statistik konnte nicht geladen werden"));
        api.get("/admin/vehicle-knowledge/weekly-digest").then((r) => setDigest(r.data.digest)).catch(() => {});
    }, []);
    if (!data) return <div className="text-center py-12 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
    const t = data.totals || {};
    const cards = [
        { label: "FIN-Präfixe gesamt", value: t.prefixes, tid: "stat-prefixes" },
        { label: "Verifiziert", value: t.verified, tid: "stat-verified" },
        { label: "KI-angereichert", value: t.enriched, tid: "stat-enriched" },
        { label: "HSN/TSN gelernt", value: t.hsn_tsn_learned, tid: "stat-hsn" },
        { label: "Gesparte KI-Abfragen", value: t.saved_ai_calls, tid: "stat-saved" },
    ];
    return (
        <div data-testid="vk-stats-tab">
            {digest && (
                <div className="mb-4 text-sm text-gray-800 bg-white border-l-4 border-[#FFCC00] border border-gray-200 rounded-sm px-4 py-3" data-testid="vk-weekly-digest">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Wochenbericht {digest.week} (wird montags automatisch an Admins gemailt)</div>
                    {digest.text}
                </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                {cards.map((c) => (
                    <div key={c.tid} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={c.tid}>
                        <div className="text-2xl font-black font-mono-num">{c.value ?? 0}</div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{c.label}</div>
                    </div>
                ))}
            </div>
            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                <table className="w-full text-sm" data-testid="vk-stats-table">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500">
                            <th className="text-left px-3 py-2">Woche</th>
                            <th className="text-right px-3 py-2">Neu gesammelt</th>
                            <th className="text-right px-3 py-2">KI-angereichert</th>
                            <th className="text-right px-3 py-2">Verifiziert</th>
                            <th className="text-right px-3 py-2">HSN/TSN gelernt</th>
                            <th className="text-right px-3 py-2">Gesparte KI-Abfragen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data.weeks || []).length === 0 ? (
                            <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">Noch keine Wochen-Daten</td></tr>
                        ) : data.weeks.map((w) => (
                            <tr key={w.week} className="border-b border-gray-100">
                                <td className="px-3 py-2 font-bold">{w.week}</td>
                                <td className="px-3 py-2 text-right font-mono-num">{w.collected}</td>
                                <td className="px-3 py-2 text-right font-mono-num">{w.enriched}</td>
                                <td className="px-3 py-2 text-right font-mono-num">{w.verified}</td>
                                <td className="px-3 py-2 text-right font-mono-num">{w.hsn_tsn_learned}</td>
                                <td className="px-3 py-2 text-right font-mono-num font-bold text-emerald-700">{w.lookup_hits}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="text-[11px] text-gray-500 mt-3">
                „Gesparte KI-Abfragen" = FIN-/HSN-TSN-Anfragen, die aus der internen Wissensdatenbank beantwortet wurden (kein LLM-Aufruf nötig).
            </p>
        </div>
    );
}

/* ------------------------------- Seite ----------------------------------- */
export default function AdminHsnTsnPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("prefix");

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "admin") navigate("/");
    }, [user, navigate]);

    if (!user) return <div className="p-8">Lade…</div>;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="admin-hsn-tsn-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                    <div className="flex items-end justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight inline-flex items-center gap-2">
                                <Database className="w-7 h-7" /> Fahrzeug-Wissensdatenbank
                            </h1>
                            <p className="text-sm text-gray-400 mt-1 truncate max-w-full">
                                FIN-Präfixe, HSN/TSN & gelernte Fahrzeugdaten — prüfen, korrigieren, per KI anreichern.
                            </p>
                        </div>
                        <div className="flex gap-2 flex-wrap justify-end" data-testid="hsn-tsn-nav-row">
                            <Link to="/admin" className="text-xs font-bold uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm inline-flex items-center" data-testid="back-to-admin">
                                ← Admin-Übersicht
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
                <div className="flex gap-1 mb-5 border-b border-gray-200">
                    <button onClick={() => setTab("prefix")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px ${tab === "prefix" ? "border-[#FFCC00] text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-800"}`} data-testid="tab-vin-prefix">
                        FIN-Präfixe
                    </button>
                    <button onClick={() => setTab("hsntsn")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px ${tab === "hsntsn" ? "border-[#FFCC00] text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-800"}`} data-testid="tab-hsn-tsn">
                        HSN/TSN (gelernt)
                    </button>
                    <button onClick={() => setTab("wartung")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px ${tab === "wartung" ? "border-[#FFCC00] text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-800"}`} data-testid="tab-maintenance-plans">
                        Wartungspläne
                    </button>
                    <button onClick={() => setTab("stats")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px ${tab === "stats" ? "border-[#FFCC00] text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-800"}`} data-testid="tab-vk-stats">
                        Statistik
                    </button>
                </div>
                {tab === "prefix" ? <VinPrefixTab /> : tab === "stats" ? <StatsTab /> : tab === "wartung" ? <MaintenancePlansTab /> : <HsnTsnTab />}

                {/* Jul-2026 (User-Wunsch) — KI-Daten-Postfach hier statt im Admin-Dashboard */}
                <div className="mt-10">
                    <KnowledgeMailCard />
                </div>
            </div>
        </div>
    );
}
